import styled from 'styled-components';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { Sheet, BadgeText, Flex, Icon, Text, HeadlessButton, ImperativeSheetRef } from '@cellula/react';
import { useFormWorkerState } from './form_manager/use_form_worker';
import { DynamicFormPageLabel } from './dynamic_form_page_label.component';
import { DynamicForm } from './dynamic_form.component';
import { forwardRef, HTMLAttributes, MouseEvent, Ref, useCallback, useEffect, useRef } from 'react';
import { withTranslation } from '@application/i18n/with_translation';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import type { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import { useResolve } from '@visiba/instantiation';
import { NotificationService } from '@component/notification_manager/notification/notification.service';
import { LocalPushNotification } from '@component/notification_manager/notification/mod';

const elements = {
	sheetInner: styled.div`
		max-width: 600px;
		width: 100%;
		margin: 0 auto;
		display: flex;
		flex-grow: 1;
		flex-direction: column;
		padding: 0 ${tokens.spacing.x2};
	`,
	container: styled(HeadlessButton)`
		padding: ${tokens.spacing.x2} ${tokens.spacing.x1_5};
		display: flex;
		align-items: center;
		gap: ${tokens.spacing.x1_5};
		background-color: ${tokens.color.bg.surface};
		border-radius: ${tokens.borderRadius.medium};
		text-align: start;
		border: 1px solid transparent;
		box-shadow: ${tokens.elevation.skim};
		justify-content: flex-start;
		overflow: hidden;
		position: relative;
		width: 100%;
		border: 1px solid ${tokens.color.border.muted};
		transition: box-shadow 150ms ease-in;
		color: ${tokens.color.content.default};

		&:hover {
			transition: box-shadow 50ms;
			box-shadow: ${tokens.elevation.lifted};
		}

		&:active {
			box-shadow: none;
		}

		&:disabled,
		&[data-disabled='true'] {
			box-shadow: none;
			opacity: 0.6;
			cursor: not-allowed;
		}

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			background-color: ${primitiveColorTokens.primary[100]};
			z-index: -1;
			opacity: 0.4;
		}

		&[data-focus='true'] {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 2px;
		}
	`,
};

interface Props {
	formWorker: Readonly<FormWorker>;
	analyticAction: string;
	disabled?: boolean;
	onCompleted?: (() => void) | false;
}

export function FormCard({ formWorker, analyticAction, disabled, onCompleted }: Props): JSX.Element {
	const ref = useRef<ImperativeSheetRef>(null);
	const notificationService = useResolve(NotificationService);

	const handleCompleted = useCallback(
		function handleCompleted() {
			if (onCompleted === false) {
				return;
			}

			if (onCompleted) {
				onCompleted();

				return;
			}

			notificationService.add(
				new LocalPushNotification({
					label: withTranslation('Feature.DynamicForms.Notification.Completed'),
					type: 'toast',
					variant: 'success',
				}),
			);
		},
		[notificationService, onCompleted],
	);

	useEffect(
		function whenMount() {
			function closeSheet() {
				ref.current?.close();
			}

			formWorker.events.on('onAbort', closeSheet);
			formWorker.events.on('onCommit', closeSheet);
			formWorker.events.on('onCompleted', handleCompleted);

			return function whenUnmount() {
				formWorker.events.off('onAbort', closeSheet);
				formWorker.events.off('onCommit', closeSheet);
				formWorker.events.off('onCompleted', handleCompleted);
			};
		},
		[formWorker, handleCompleted],
	);

	return (
		<Sheet
			ref={ref}
			variant='full-restricted'
			label={<DynamicFormPageLabel formWorker={formWorker} />}
			key={formWorker.formId}
			trigger={<Trigger disabled={disabled} analyticAction={analyticAction} formWorker={formWorker} />}
		>
			<elements.sheetInner>
				<DynamicForm.Root key={formWorker.formId} formWorker={formWorker}>
					<Flex gap={tokens.spacing.x2} grow={1}>
						<DynamicForm.Description />
						<DynamicForm.Content>
							<DynamicForm.Actions />
						</DynamicForm.Content>
					</Flex>
				</DynamicForm.Root>
			</elements.sheetInner>
		</Sheet>
	);
}

interface TriggerProps extends HTMLAttributes<HTMLButtonElement> {
	formWorker: Readonly<FormWorker>;
	analyticAction: string;
	disabled?: boolean;
}

function _Trigger({ formWorker, analyticAction, disabled, onClick, ...rest }: TriggerProps, ref: Ref<HTMLButtonElement>): JSX.Element {
	const { completed } = useFormWorkerState(formWorker);
	const analyticCtx = useAnalyticContext();

	function handleClick(event: MouseEvent<HTMLButtonElement>): void {
		if (onClick != null) {
			onClick(event);
		}

		// TODO: Make useAnalyticContext return a function wrapping queueMicrotask,
		// can't have events bloating up the main thread.
		queueMicrotask(() => {
			analyticCtx.trackEvent(analyticAction);
		});
	}

	return (
		<elements.container ref={ref} tabIndex={disabled ? -1 : undefined} onClick={handleClick} {...rest}>
			<Icon type='notesBoardPencil' />

			<Flex grow={1}>
				<Text variant='bodySemiBold'>{formWorker.header}</Text>
			</Flex>

			{completed ? (
				<BadgeText variant='success' icon='check' size='small'>
					{withTranslation('Feature.DynamicForms.FormCard.Label.Answered')}
				</BadgeText>
			) : (
				<BadgeText variant='interactive' size='small'>
					{withTranslation('Feature.DynamicForms.FormCard.Label.Unanswered')}
				</BadgeText>
			)}
		</elements.container>
	);
}
const Trigger = forwardRef(_Trigger);
